import React, { createContext, useContext, useState, useEffect, useSyncExternalStore } from 'react';
import { useParams } from 'react-router-dom';
import StoreService from '../services/storeService';
import requestDeduplicator from '../utils/requestDeduplicator';
import { isValidGuid, getStoreSlugFromHost } from '../utils/storeUtils';
import { applyStoreDocumentMeta, resetDocumentMeta } from '../utils/documentMeta';
import { getLocalizedValue, getPreferredLanguage } from '../constants/Languages';

const StoreContext = createContext(null);

const STORE_NAME_STORAGE_KEY = 'currentStoreName';
const STORE_SLUG_STORAGE_KEY = 'currentStoreSlug';

const readCachedStoreName = ({ storeId, slug } = {}) => {
  if (typeof window === 'undefined') return null;

  try {
    const cachedName = localStorage.getItem(STORE_NAME_STORAGE_KEY);
    if (!cachedName) return null;

    if (storeId) {
      const cachedStoreId = localStorage.getItem('currentStoreId');
      if (cachedStoreId === storeId) return cachedName;
    }

    if (slug) {
      const cachedSlug = localStorage.getItem(STORE_SLUG_STORAGE_KEY);
      if (cachedSlug === slug) return cachedName;
    }
  } catch {
    // ignore storage errors
  }

  return null;
};

const persistStoreIdentity = ({ storeId, slug, name }) => {
  if (typeof window === 'undefined') return;

  try {
    if (storeId) {
      localStorage.setItem('currentStoreId', storeId);
    }
    if (slug) {
      localStorage.setItem(STORE_SLUG_STORAGE_KEY, slug);
    }
    if (name) {
      localStorage.setItem(STORE_NAME_STORAGE_KEY, name);
    }
  } catch {
    // ignore storage errors
  }
};

let customerStoreNameSnapshot = typeof window !== 'undefined'
  ? localStorage.getItem(STORE_NAME_STORAGE_KEY)
  : null;
const customerStoreNameListeners = new Set();

const emitCustomerStoreName = (name) => {
  customerStoreNameSnapshot = name || null;
  customerStoreNameListeners.forEach((listener) => listener());
};

export const useCustomerStoreName = () => useSyncExternalStore(
  (listener) => {
    customerStoreNameListeners.add(listener);
    return () => customerStoreNameListeners.delete(listener);
  },
  () => customerStoreNameSnapshot,
  () => null,
);

const isNotFoundError = (error) => error?.response?.status === 404;

const applyOverview = (overview, { slug, setResolvedStoreId, setStoreOverview, setStoreName }) => {
  if (!overview) return false;

  if (overview.id) {
    setResolvedStoreId?.(overview.id);
  }

  setStoreOverview(overview);

  if (overview.name) {
    setStoreName(overview.name);
    persistStoreIdentity({
      storeId: overview.id,
      slug,
      name: overview.name,
    });
  }

  return true;
};

export const StoreProvider = ({ children }) => {
  const { storeId: routeStoreId } = useParams();
  const slug = getStoreSlugFromHost();
  const subdomainMode = slug !== null;

  const storedStoreId = typeof window !== 'undefined' ? localStorage.getItem('currentStoreId') : null;
  const cachedSlug = typeof window !== 'undefined' ? localStorage.getItem(STORE_SLUG_STORAGE_KEY) : null;
  const slugMatchesCache = Boolean(subdomainMode && slug && cachedSlug === slug);
  const pathStoreId = !subdomainMode
    ? (isValidGuid(routeStoreId) ? routeStoreId : (isValidGuid(storedStoreId) ? storedStoreId : null))
    : null;

  const [resolvedStoreId, setResolvedStoreId] = useState(() => (
    slugMatchesCache && isValidGuid(storedStoreId) ? storedStoreId : null
  ));
  const storeId = subdomainMode ? resolvedStoreId : pathStoreId;

  const [storeName, setStoreName] = useState(() => readCachedStoreName({
    storeId: pathStoreId || (slugMatchesCache ? storedStoreId : null),
    slug: subdomainMode ? slug : null,
  }));
  const [storeOverview, setStoreOverview] = useState(null);
  const [loading, setLoading] = useState(() => Boolean(
    (subdomainMode && slug) || (!subdomainMode && pathStoreId)
  ));
  const [notFound, setNotFound] = useState(false);

  const markStoreNotFound = () => {
    setNotFound(true);
    setResolvedStoreId(null);
    setStoreOverview(null);
    setStoreName(null);
  };

  // Persist the storeId from the URL (path mode only).
  useEffect(() => {
    if (!subdomainMode && routeStoreId && isValidGuid(routeStoreId)) {
      const previousStoreId = localStorage.getItem('currentStoreId');
      if (previousStoreId !== routeStoreId) {
        localStorage.removeItem(STORE_NAME_STORAGE_KEY);
        localStorage.removeItem(STORE_SLUG_STORAGE_KEY);
      }
      localStorage.setItem('currentStoreId', routeStoreId);
    }
  }, [routeStoreId, subdomainMode]);

  // Subdomain mode: resolve the store (and its id) from the slug.
  useEffect(() => {
    if (!subdomainMode || !slug) return;

    let cancelled = false;
    setLoading(true);
    setNotFound(false);

    const cachedSlugMatches = typeof window !== 'undefined'
      && localStorage.getItem(STORE_SLUG_STORAGE_KEY) === slug;

    const requestKey = `store-overview-slug-${slug}`;
    requestDeduplicator
      .deduplicate(requestKey, () => StoreService.getCustomerStoreOverviewBySlug(slug))
      .then((overview) => {
        if (cancelled) return;
        if (!applyOverview(overview, { slug, setResolvedStoreId, setStoreOverview, setStoreName })) {
          markStoreNotFound();
        }
      })
      .catch((error) => {
        console.error('Error resolving store by slug:', error);
        if (cancelled) return;
        if (isNotFoundError(error) || !cachedSlugMatches) {
          markStoreNotFound();
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [subdomainMode, slug]);

  // Path mode: fetch store overview by storeId - deduplicated so only one
  // request per storeId (handles Strict Mode double-mount).
  useEffect(() => {
    if (subdomainMode || !storeId) return;

    let cancelled = false;
    setLoading(true);

    const cachedName = readCachedStoreName({ storeId });
    if (cachedName) {
      setStoreName(cachedName);
    }

    const requestKey = `store-overview-${storeId}`;
    requestDeduplicator
      .deduplicate(requestKey, () => StoreService.getCustomerStoreOverview(storeId))
      .then((overview) => {
        if (cancelled) return;
        if (!applyOverview(overview, { setResolvedStoreId, setStoreOverview, setStoreName })) {
          markStoreNotFound();
        }
      })
      .catch((error) => {
        console.error('Error loading store overview:', error);
        if (cancelled) return;
        if (isNotFoundError(error) || !readCachedStoreName({ storeId })) {
          markStoreNotFound();
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [storeId, subdomainMode]);

  useEffect(() => {
    emitCustomerStoreName(storeName);
  }, [storeName]);

  useEffect(() => {
    if (notFound || !storeName) {
      if (notFound) {
        resetDocumentMeta();
      }
      return undefined;
    }

    const description = getLocalizedValue(
      storeOverview?.about,
      getPreferredLanguage(),
      storeOverview?.storeLanguage,
      storeName,
    );

    applyStoreDocumentMeta({
      title: storeName,
      appTitle: storeOverview?.pwaAppName || storeName,
      description: description || storeName,
      url: window.location.href,
      imageUrl: storeOverview?.logoUrl || storeOverview?.images?.[0]?.url,
      faviconUrl: storeOverview?.brandingImages?.faviconUrl,
      iconVersion: storeOverview?.pwaIconVersion,
    });
  }, [storeName, storeOverview, notFound]);

  useEffect(() => () => {
    resetDocumentMeta();
  }, []);

  const value = {
    storeId,
    storeSlug: slug,
    subdomainMode,
    storeName,
    storeOverview, // Share the full overview data to prevent duplicate API calls
    storeConfig: null,
    loading,
    notFound,
  };

  return (
    <StoreContext.Provider value={value}>
      {children}
    </StoreContext.Provider>
  );
};

export const useStore = () => {
  const context = useContext(StoreContext);
  if (!context) {
    throw new Error('useStore must be used within a StoreProvider');
  }
  return context;
};

export const useOptionalStore = () => useContext(StoreContext);
